// Smart Document Drop & Intelligent Triage shared client contract.
// Supports zero-login external drop portals (/drop/:token) for Brokers/LOs, Processors, and Borrowers,
// and internal multi-role upload & intelligent triage (/smart-drops) across AE, CA, AM, and Processor.
//
// AGENTS.md invariant: Never invent, guess, or approximate underwriting rules, thresholds, rates, or pricing.
// Classification only identifies document category, Laya 16-class document family, detected borrower name
// from file metadata, statement period, Four-Legs pillar, and internal reviewer seat routing.

import { ApiError, api } from "./api";
import {
  LAYA_ARCHIVE_C3_CHECKPOINT,
  LAYA_ARCHIVE_C4_CHECKPOINT,
  LAYA_ARCHIVE_C5_CHECKPOINT,
  LAYA_ARCHIVE_C5_EMAIL_CHECKPOINT,
  LAYA_ARCHIVE_C6_CHECKPOINT,
  LAYA_ARCHIVE_C6_EMAIL_CHECKPOINT,
  LAYA_ARCHIVE_C7_CHECKPOINT,
  LAYA_ARCHIVE_C7_EMAIL_CHECKPOINT,
  LAYA_ARCHIVE_C8_CHECKPOINT,
  LAYA_ARCHIVE_C8_EMAIL_CHECKPOINT,
  LAYA_ARCHIVE_C9_CHECKPOINT,
  LAYA_ARCHIVE_C9_EMAIL_CHECKPOINT,
  extractLayaStructuralFeatures,
  extractStatementPeriod as extractPrintedStatementPeriod,
  analyzeStatementContinuity,
  extractEmailTriage,
  computeStatementSeriesContinuity,
  propagateBatchSiblingContext,
  computeBatchPackageSummary,
  inferConditionTargetTags,
  inferDocumentTargetTags,
  matchOpenCondition,
  extractDocumentBorrowerOrEntity,
  extractPropertyAddress,
  extractLoanNumber,
  extractDocumentCompletenessDiagnostics,
  type BatchPackageSummary,
  type CandidateLoanForBatch,
  type ConditionTargetFamily,
  type DocumentCompletenessDiagnostics,
  type DocumentCompletenessWarning,
  type EmailTriageMetadata,
  type StatementSeriesContinuityEntry,
  type StatementSeriesContinuityReport,
} from "./smartDropPortal";

export {
  LAYA_ARCHIVE_C3_CHECKPOINT,
  LAYA_ARCHIVE_C4_CHECKPOINT,
  LAYA_ARCHIVE_C5_CHECKPOINT,
  LAYA_ARCHIVE_C5_EMAIL_CHECKPOINT,
  LAYA_ARCHIVE_C6_CHECKPOINT,
  LAYA_ARCHIVE_C6_EMAIL_CHECKPOINT,
  LAYA_ARCHIVE_C7_CHECKPOINT,
  LAYA_ARCHIVE_C7_EMAIL_CHECKPOINT,
  LAYA_ARCHIVE_C8_CHECKPOINT,
  LAYA_ARCHIVE_C8_EMAIL_CHECKPOINT,
  LAYA_ARCHIVE_C9_CHECKPOINT,
  LAYA_ARCHIVE_C9_EMAIL_CHECKPOINT,
  extractLayaStructuralFeatures,
  extractPrintedStatementPeriod,
  analyzeStatementContinuity,
  extractEmailTriage,
  computeStatementSeriesContinuity,
  propagateBatchSiblingContext,
  computeBatchPackageSummary,
  inferConditionTargetTags,
  inferDocumentTargetTags,
  matchOpenCondition,
  extractDocumentBorrowerOrEntity,
  extractPropertyAddress,
  extractLoanNumber,
  extractDocumentCompletenessDiagnostics,
  type BatchPackageSummary,
  type CandidateLoanForBatch,
  type ConditionTargetFamily,
  type DocumentCompletenessDiagnostics,
  type DocumentCompletenessWarning,
  type EmailTriageMetadata,
  type StatementSeriesContinuityEntry,
  type StatementSeriesContinuityReport,
};

export type PortalRole = "broker_lo" | "processor" | "loan_processor" | "borrower";

export type UploaderRole =
  | "broker_lo"
  | "processor"
  | "loan_processor"
  | "borrower"
  | "AE"
  | "CA"
  | "AM"
  | "Processor"
  | "ae"
  | "ca"
  | "am"
  | "internal_processor";

export type DocCategory =
  | "mismo_1003"
  | "1003_mismo"
  | "bank_statements"
  | "bank_statement"
  | "tax_returns"
  | "income_docs"
  | "credit_report"
  | "asset_statements"
  | "appraisal_valuation"
  | "appraisal"
  | "lease_rent_roll"
  | "title_escrow"
  | "insurance_hoi"
  | "entity_docs"
  | "purchase_contract"
  | "condition_clearing"
  | "disclosures_le_cd"
  | "other";

export type LayaDocumentFamily =
  | "bank_statement"
  | "paystub"
  | "credit_report"
  | "tax_return"
  | "tax_form"
  | "mortgage_statement"
  | "credit_card_statement"
  | "utility_bill"
  | "insurance"
  | "appraisal"
  | "lease"
  | "application"
  | "invoice"
  | "other"
  | "unknown"
  | "mixed_page";

export type FourLegsPillar =
  | "Income"
  | "Credit"
  | "Equity"
  | "Assets"
  | "Application"
  | "Conditions";

export type RoutedSeat = "AE" | "CA" | "AM" | "Processor";

export type ReviewStatus =
  | "pending_triage"
  | "pending_review"
  | "pending"
  | "needs_review"
  | "accepted"
  | "bound_to_loan"
  | "attached_to_condition"
  | "archived"
  | "rejected";

export type PageIntegrityStatus =
  | "verified_complete"
  | "missing_pages_detected"
  | "screenshot_warning"
  | "unverified";

export interface SmartDropExtractedHighlights {
  laya_family?: LayaDocumentFamily | string;
  secondary_laya_families?: string[];
  is_multi_document_bundle?: boolean;
  statement_period?:
    | string
    | { status?: string; start?: string; end?: string; reason?: string }
    | null;
  segmentation_guard_triggered?: boolean;
  model_checkpoint?: string;
  structural_features?: string[];
  email_triage?: EmailTriageMetadata | null;
  source_filename?: string;
  format?: string;
  account_hint?: string | null;
  page_integrity?:
    | PageIntegrityStatus
    | {
        status?: PageIntegrityStatus;
        detected_pages?: number[];
        expected_pages?: number | null;
        missing_pages?: number[];
        advisory?: string | null;
      };
  missing_pages_summary?: string | null;
  account_last4?: string | null;
  institution_name?: string | null;
  proactive_loe_kind?: string | null;
  native_signature_seal?: string | null;
  ls2_native_signature?: string | Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface NativeSignRequestItem {
  id: string;
  borrower_name: string;
  signer_role: PortalRole;
  document_kind: string;
  document_title: string;
  status: "pending" | "signed";
  signature_seal_sha256?: string | null;
  created_at: string;
  sign_url: string;
  item_id?: string | null;
  loan_id?: string | null;
  person_id?: string | null;
  token?: string | null;
  requested_by_role?: UploaderRole;
  signed_by?: string | null;
  signed_at?: string | null;
}

export interface SmartDropItem {
  id: string;
  token?: string | null;
  link_id?: string | null;
  filename: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  sha256: string;
  uploaded_at: string;
  created_at?: string;
  uploader_role: UploaderRole;
  uploader_name?: string | null;
  uploader_email?: string | null;
  person_id?: string | null;
  company_id?: string | null;
  lo_name?: string | null;
  company_name?: string | null;
  deal_link_id?: string | null;
  loan_id?: string | null;
  condition_id?: string | null;
  matched_condition_id?: string | null;
  detected_borrower_name: string;
  borrower_name: string;
  detected_co_borrower_name?: string | null;
  detected_property_address?: string | null;
  detected_loan_number?: string | null;
  doc_category: DocCategory;
  doc_type_label: string;
  four_legs_pillar: FourLegsPillar;
  routed_seat: RoutedSeat;
  routing_reason: string;
  confidence?: "high" | "medium" | "low";
  extracted_highlights?: SmartDropExtractedHighlights;
  extracted_facts?: Record<string, string | number | boolean | null>;
  laya_family?: LayaDocumentFamily | string;
  statement_period?: string | null;
  segmentation_guard_triggered?: boolean;
  page_integrity?: PageIntegrityStatus;
  missing_pages_summary?: string | null;
  account_last4?: string | null;
  institution_name?: string | null;
  proactive_loe_kind?: string | null;
  native_signature_seal?: string | null;
  review_status: ReviewStatus;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  notes?: string | null;
}

export interface SmartDropLink {
  id: string;
  token: string;
  portal_role: PortalRole;
  recipient_email?: string | null;
  person_id?: string | null;
  company_id?: string | null;
  lo_name?: string | null;
  company_name?: string | null;
  deal_link_id?: string | null;
  loan_id?: string | null;
  condition_id?: string | null;
  borrower_name?: string | null;
  label?: string | null;
  created_at: string;
  expires_at?: string | null;
  revoked_at?: string | null;
  upload_count?: number;
  url?: string;
}

export interface PipelineLoanOption {
  id: string;
  loan_number: string;
  borrower_name: string;
  lo_name?: string | null;
  company_name?: string | null;
  stage?: string | null;
  amount?: number | null;
}

export interface BorrowerDropGroup {
  borrower_key: string;
  borrowerKey: string;
  borrower_name: string;
  borrowerName: string;
  detected_borrower_name: string;
  lo_name: string | null;
  company_name: string | null;
  person_id: string | null;
  loan_id: string | null;
  items: SmartDropItem[];
  pillars: FourLegsPillar[];
  fourLegsPillars: FourLegsPillar[];
  seats: RoutedSeat[];
  routedSeats: RoutedSeat[];
  latest_uploaded_at: string;
}

export interface PublicDropPortalInfo {
  link: SmartDropLink;
  portal_role: PortalRole;
  recipient_email?: string | null;
  lo_name: string | null;
  company_name: string | null;
  borrower_name?: string | null;
  loan_id?: string | null;
  condition_id?: string | null;
  recent_items: SmartDropItem[];
  items?: SmartDropItem[];
  borrower_groups?: BorrowerDropGroup[];
}

export interface SmartDropInboxResponse {
  items: SmartDropItem[];
  links?: SmartDropLink[];
  counts_by_seat?: Record<RoutedSeat, number>;
  counts_by_pillar?: Record<FourLegsPillar, number>;
  total: number;
}

export interface FileClassificationPreview {
  detected_borrower_name: string;
  doc_category: DocCategory;
  doc_type_label: string;
  four_legs_pillar: FourLegsPillar;
  routed_seat: RoutedSeat;
  routing_reason: string;
  confidence: "high" | "medium" | "low";
  laya_family: LayaDocumentFamily;
  statement_period: string | null;
  segmentation_guard_triggered: boolean;
  page_integrity: PageIntegrityStatus;
  missing_pages_summary: string | null;
  account_last4: string | null;
  institution_name: string | null;
  proactive_loe_kind: string | null;
  native_signature_seal: string | null;
  extracted_highlights: SmartDropExtractedHighlights;
  extracted_facts: Record<string, string | number | boolean | null>;
}

export interface SmartDropUploadPayload {
  filename: string;
  file_name?: string;
  mime_type: string;
  size_bytes: number;
  byte_size?: number;
  sha256: string;
  detected_borrower_name: string;
  doc_category: DocCategory;
  doc_type_label: string;
  four_legs_pillar: FourLegsPillar;
  routed_seat: RoutedSeat;
  routing_reason: string;
  laya_family?: LayaDocumentFamily;
  statement_period?: string | null;
  segmentation_guard_triggered?: boolean;
  extracted_highlights?: SmartDropExtractedHighlights;
  extracted_facts?: Record<string, string | number | boolean | null>;
  content_excerpt?: string;
}

export const DOC_CATEGORY_LABELS: Record<DocCategory, string> = {
  mismo_1003: "1003 / MISMO 3.4 Loan Application",
  "1003_mismo": "1003 / MISMO 3.4 Loan Application",
  bank_statements: "12/24-Mo Bank Statements",
  bank_statement: "12/24-Mo Bank Statements",
  tax_returns: "Income / Tax / CPA Documentation",
  income_docs: "Income / Tax / CPA Documentation",
  credit_report: "Tri-Merge Credit Report / Supplement",
  asset_statements: "Asset & Post-Close Reserve Statements",
  appraisal_valuation: "Appraisal & Collateral Valuation",
  appraisal: "Appraisal & Collateral Valuation",
  lease_rent_roll: "Lease Agreement / DSCR Rent Roll",
  title_escrow: "Preliminary Title & Escrow Package",
  insurance_hoi: "Hazard / Flood Insurance (HOI)",
  entity_docs: "Entity / LLC Vesting Package",
  purchase_contract: "Executed Purchase Contract (PSA)",
  condition_clearing: "Condition Clearing / Stips Evidence",
  disclosures_le_cd: "Signed Disclosures / Fee Confirmation",
  other: "Supporting Loan Document",
};

export const LAYA_FAMILY_LABELS: Record<LayaDocumentFamily, string> = {
  bank_statement: "Laya · bank_statement",
  paystub: "Laya · paystub",
  credit_report: "Laya · credit_report",
  tax_return: "Laya · tax_return",
  tax_form: "Laya · tax_form",
  mortgage_statement: "Laya · mortgage_statement",
  credit_card_statement: "Laya · credit_card_statement",
  utility_bill: "Laya · utility_bill",
  insurance: "Laya · insurance",
  appraisal: "Laya · appraisal",
  lease: "Laya · lease",
  application: "Laya · application",
  invoice: "Laya · invoice",
  other: "Laya · other",
  unknown: "Laya · unknown",
  mixed_page: "Laya · mixed_page",
};

export const FOUR_LEGS_LABELS: Record<FourLegsPillar, string> = {
  Income: "Income Pillar",
  Credit: "Credit Pillar",
  Equity: "Equity / Collateral Pillar",
  Assets: "Assets / Reserves Pillar",
  Application: "1003 Application Intake",
  Conditions: "Conditions & Disclosures",
};

export const PORTAL_ROLE_LABELS: Record<PortalRole, string> = {
  broker_lo: "Broker / LO Link",
  processor: "Loan Processor Link",
  loan_processor: "Loan Processor Link",
  borrower: "Borrower Link",
};

export const UPLOADER_ROLE_LABELS: Record<UploaderRole, string> = {
  broker_lo: "Broker / LO",
  processor: "Loan Processor",
  loan_processor: "Loan Processor",
  borrower: "Borrower",
  AE: "Account Executive (AE)",
  CA: "Credit Analyst (CA)",
  AM: "Account Manager (AM)",
  Processor: "Loan Processor",
  ae: "Account Executive (AE)",
  ca: "Credit Analyst (CA)",
  am: "Account Manager (AM)",
  internal_processor: "Loan Processor",
};

export const ROUTED_SEAT_LABELS: Record<RoutedSeat, string> = {
  AE: "AE · Pre-Qual & 1003 Intake",
  CA: "CA · 4-Legs Underwriting",
  AM: "AM · Condition Clearing & Disclosures",
  Processor: "Processor · Property, Title & Entity",
};

export const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  pending_triage: "Pending Triage",
  pending_review: "Pending Triage",
  pending: "Pending Triage",
  needs_review: "Needs Review",
  accepted: "Accepted",
  bound_to_loan: "Bound to Loan",
  attached_to_condition: "Attached to Condition",
  archived: "Archived",
  rejected: "Rejected",
};

// In-memory store for session durability and offline/test resilience without polluting sessionStorage.
const memoryLinks = new Map<string, SmartDropLink>();
const memoryItems = new Map<string, SmartDropItem>();
const memorySignRequests = new Map<string, NativeSignRequestItem>();
let idCounter = 1;

export function resetSmartDropMemoryStore(): void {
  memoryLinks.clear();
  memoryItems.clear();
  memorySignRequests.clear();
  idCounter = 1;
}

export function buildSmartDropUrl(token: string, origin?: string): string {
  const base =
    origin ??
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "");
  const cleanToken = token.startsWith("lsdrop_") ? token : `lsdrop_${token}`;
  return `${base}/drop/${encodeURIComponent(cleanToken)}`;
}

export function buildSmartPosUrl(token: string, origin?: string): string {
  const base =
    origin ??
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "");
  const cleanToken = token.startsWith("lsdrop_") ? token : `lsdrop_${token}`;
  return `${base}/pos/${encodeURIComponent(cleanToken)}`;
}

export function buildSmartSignUrl(token: string, origin?: string): string {
  const base =
    origin ??
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "");
  const cleanToken = token.startsWith("lsdrop_") ? token : `lsdrop_${token}`;
  return `${base}/sign/${encodeURIComponent(cleanToken)}`;
}

export const buildNativeSignUrl = buildSmartSignUrl;

const DOC_STOPWORDS = new Set([
  "1003",
  "mismo",
  "urla",
  "fnm",
  "application",
  "app",
  "bank",
  "stmt",
  "stmts",
  "statement",
  "statements",
  "12mo",
  "24mo",
  "12month",
  "24month",
  "chase",
  "wells",
  "fargo",
  "bofa",
  "citi",
  "mercury",
  "first",
  "republic",
  "america",
  "acct",
  "account",
  "missing",
  "pages",
  "gap",
  "wire",
  "deposit",
  "deposits",
  "large",
  "inquiry",
  "inquiries",
  "screenshot",
  "screen",
  "shot",
  "verified",
  "complete",
  "tax",
  "return",
  "returns",
  "1040",
  "w2",
  "1099",
  "k1",
  "pnl",
  "cpa",
  "letter",
  "credit",
  "report",
  "trimerge",
  "fico",
  "supplement",
  "asset",
  "assets",
  "reserve",
  "reserves",
  "fidelity",
  "schwab",
  "vanguard",
  "vod",
  "appraisal",
  "1004",
  "1007",
  "bpo",
  "valuation",
  "lease",
  "rent",
  "roll",
  "agreement",
  "dscr",
  "title",
  "prelim",
  "preliminary",
  "commitment",
  "escrow",
  "payoff",
  "hoi",
  "hazard",
  "insurance",
  "flood",
  "binder",
  "dec",
  "page",
  "llc",
  "operating",
  "articles",
  "ein",
  "entity",
  "vesting",
  "purchase",
  "contract",
  "psa",
  "executed",
  "addendum",
  "condition",
  "conditions",
  "clearing",
  "stip",
  "stips",
  "loe",
  "explanation",
  "disclosure",
  "disclosures",
  "le",
  "cd",
  "signed",
  "fee",
  "sheet",
  "pdf",
  "xml",
  "json",
  "csv",
  "png",
  "jpg",
  "jpeg",
  "doc",
  "docx",
  "final",
  "draft",
  "v1",
  "v2",
  "copy",
  "scan",
  "mixed",
  "paystub",
  "invoice",
  "utility",
  "borrower",
  "borrowers",
  "coborrower",
  "client",
  "applicant",
  "packet",
  "bundle",
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
  "airdna",
  "rentalizer",
  "overview",
  "review",
  "pq",
  "guide",
  "completed",
  "adm",
  "tech",
  "initial",
  "estmt",
]);

function titleCaseWord(w: string): string {
  if (!w) return "";
  if (/^(llc|inc|lp|llp|pllc|trust)$/i.test(w)) return w.toUpperCase();
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

function extractBorrowerFromFilename(
  rawFilename: string,
  hintBorrower?: string | null,
  contentExcerpt?: string | null,
): string {
  if (hintBorrower && hintBorrower.trim()) {
    return hintBorrower.trim();
  }
  const fromDoc = extractDocumentBorrowerOrEntity(contentExcerpt);
  if (fromDoc) {
    return fromDoc;
  }
  const withoutExt = rawFilename.replace(/\.[a-z0-9]{1,6}$/i, "");
  const commaParts = withoutExt.split(",");
  if (
    commaParts.length === 2 &&
    /^[a-zA-Z' -]+$/.test(commaParts[0]?.trim() ?? "") &&
    /^[a-zA-Z' -]+$/.test(commaParts[1]?.trim() ?? "")
  ) {
    const last = commaParts[0]!.trim();
    const first = commaParts[1]!.trim().split(/\s+/)[0] ?? "";
    if (first && last) {
      return `${titleCaseWord(first)} ${titleCaseWord(last)}`;
    }
  }

  const tokens = withoutExt
    .split(/[\s_\-·.,()[\]]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const nameTokens: string[] = [];
  for (const token of tokens) {
    const lower = token.toLowerCase();
    if (/^\d+$/.test(token) || /^20\d\d$/.test(token)) continue;
    if (DOC_STOPWORDS.has(lower)) {
      if (nameTokens.length >= 2) break;
      continue;
    }
    if (/^[a-zA-Z'&]+$/.test(token) && token.length >= 2) {
      nameTokens.push(token);
      if (nameTokens.length >= 2) {
        break;
      }
    }
  }

  if (nameTokens.length >= 2) {
    return nameTokens.map(titleCaseWord).join(" ");
  }

  return "Unassigned Borrower (Pending Match)";
}

export function extractStatementPeriod(combined: string): string | null {
  const spaced = combined.replace(/_/g, " ");
  const rangeMatch = spaced.match(
    /\b(20\d{2}[-/](?:0[1-9]|1[0-2])\s*(?:to|thru|-|–)\s*20\d{2}[-/](?:0[1-9]|1[0-2]))\b/i,
  );
  if (rangeMatch?.[1]) return rangeMatch[1];

  const printed = extractPrintedStatementPeriod(combined);
  if (printed.status === "source_extracted" && printed.start && printed.end) {
    return `${printed.start} to ${printed.end}`;
  }

  const monthRangeMatch = spaced.match(
    /\b((?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[\s-]+(?:to[\s-]+|thru[\s-]+|-)?(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[\s-]+20\d{2})\b/i,
  );
  if (monthRangeMatch?.[1]) {
    return monthRangeMatch[1].replace(/[-]+/g, " ").toUpperCase();
  }

  const ymMatch = spaced.match(/\b(20\d{2}[-](?:0[1-9]|1[0-2]))\b/);
  if (ymMatch?.[1]) return ymMatch[1];

  const multiMoYear = spaced.match(/\b(12mo|24mo|12-mo|24-mo)\b.*?\b(20\d{2}(?:[-]20\d{2})?)\b/i);
  if (multiMoYear?.[1] && multiMoYear?.[2]) {
    return `${multiMoYear[1].toUpperCase()} (${multiMoYear[2]})`;
  }

  const multiMoOnly = spaced.match(/\b(12mo|24mo|12-mo|24-mo)\b/i);
  if (multiMoOnly?.[1]) {
    return `${multiMoOnly[1].toUpperCase()} Period`;
  }

  const yearMatch = spaced.match(/\b(202[2-9])\b/);
  if (yearMatch?.[1]) return `TY ${yearMatch[1]}`;

  return null;
}

function extractPreUnderwritingDiagnostics(
  filename: string,
  rawCombined: string,
  doc_category: DocCategory,
  segmentation_guard_triggered: boolean,
): {
  page_integrity: PageIntegrityStatus;
  missing_pages_summary: string | null;
  account_last4: string | null;
  institution_name: string | null;
  proactive_loe_kind: string | null;
  native_signature_seal: string | null;
} {
  const spaced = `${filename} ${rawCombined}`.replace(/[_.]+/g, " ");
  const hasMissingPages =
    /\b(missing_page_gap|missing_pages|missing_page|pages_\d+_\d+_missing|pages?\s+\d+\s*(?:and|to|-|_)\s*\d+\s*missing|missing\s+pages?|page\s+gap|page_gap|incomplete\s+pages?)\b/i.test(
      `${rawCombined} ${spaced}`,
    ) ||
    (/\bpage\s+1\s+of\s+[2-9]\b/i.test(spaced) &&
      /\b(missing|gap|omitted|only)\b/i.test(spaced));
  const isScreenshot =
    /\.(png|jpg|jpeg|heic|webp)$/i.test(filename) ||
    /\b(screenshot|screen_shot|screen shot|camera_roll|mobile_photo)\b/i.test(
      `${rawCombined} ${spaced}`,
    );

  const page_integrity: PageIntegrityStatus = hasMissingPages
    ? "missing_pages_detected"
    : isScreenshot
      ? "screenshot_warning"
      : segmentation_guard_triggered
        ? "unverified"
        : "verified_complete";

  const missing_pages_summary = hasMissingPages
    ? "Missing pages detected"
    : null;

  const acctMatch = spaced.match(
    /(?:acct|account|ending\s+in|xx+|\*+|#)\s*[-#:]?\s*(\d{4})\b/i,
  );
  const account_last4 = acctMatch?.[1] ?? null;

  let institution_name: string | null = null;
  if (/\b(chase|jpmorgan)\b/i.test(spaced)) {
    institution_name = "Chase";
  } else if (/\b(wells\s+fargo|wells)\b/i.test(spaced)) {
    institution_name = "Wells Fargo";
  } else if (/\b(bank\s+of\s+america|bofa)\b/i.test(spaced)) {
    institution_name = "Bank of America";
  } else if (/\bmercury\b/i.test(spaced)) {
    institution_name = "Mercury";
  } else if (/\brelay(?!\s+calls)\b/i.test(spaced)) {
    institution_name = "Relay";
  } else if (/\bfirst\s+republic\b/i.test(spaced)) {
    institution_name = "First Republic";
  } else if (/\bcitibank|\bciti\b/i.test(spaced)) {
    institution_name = "Citi";
  } else if (/\bfidelity\b/i.test(spaced)) {
    institution_name = "Fidelity";
  } else if (/\bschwab\b/i.test(spaced)) {
    institution_name = "Charles Schwab";
  } else if (/\bvanguard\b/i.test(spaced)) {
    institution_name = "Vanguard";
  } else if (/\bpnc\b/i.test(spaced)) {
    institution_name = "PNC";
  } else if (/\bu\.?s\.?\s+bank\b/i.test(spaced)) {
    institution_name = "US Bank";
  } else if (/\bcapital\s+one\b/i.test(spaced)) {
    institution_name = "Capital One";
  } else if (/\btd\s+bank\b/i.test(spaced)) {
    institution_name = "TD Bank";
  } else if (/\bvenmo\b/i.test(spaced)) {
    institution_name = "Venmo";
  } else if (/\bpaypal\b/i.test(spaced)) {
    institution_name = "PayPal";
  } else if (/\b(thrift\s+savings\s+plan|tsp)\b/i.test(spaced)) {
    institution_name = "Thrift Savings Plan";
  } else if (/\ballegacy\b/i.test(spaced)) {
    institution_name = "Allegacy";
  } else if (/\bbank\s+of\s+the\s+sierra\b/i.test(spaced)) {
    institution_name = "Bank of the Sierra";
  } else if (/\bnoble\s+bank\b/i.test(spaced)) {
    institution_name = "Noble Bank";
  } else if (/\bselene\s+finance\b/i.test(spaced)) {
    institution_name = "Selene Finance";
  }

  let proactive_loe_kind: string | null = null;
  if (doc_category === "mismo_1003" || doc_category === "1003_mismo") {
    proactive_loe_kind = "urla_1003_originator";
  } else if (
    doc_category === "credit_report" ||
    /\b(credit\s+inquiry|credit_inquiry|inquiries)\b/i.test(spaced)
  ) {
    proactive_loe_kind = "loe_credit_inquiry";
  } else if (
    doc_category === "bank_statements" ||
    doc_category === "bank_statement" ||
    /\b(large\s+deposit|large_deposit|wire\s+deposit|wire\s+transfer|unsourced\s+deposit)\b/i.test(
      spaced,
    )
  ) {
    proactive_loe_kind = "loe_large_deposit";
  } else if (doc_category === "condition_clearing") {
    proactive_loe_kind = "loe_condition_clearing";
  }

  const native_signature_seal = /\b(ls2_seal|ls2-seal|ls2_native_signature|native_signature_seal|signed_via_ls2)\b/i.test(
    `${rawCombined} ${spaced}`,
  )
    ? "LS2-SEAL-VERIFIED"
    : null;

  return {
    page_integrity,
    missing_pages_summary,
    account_last4,
    institution_name,
    proactive_loe_kind,
    native_signature_seal,
  };
}

function buildPreviewResult(input: {
  filename: string;
  rawCombined?: string;
  detected_borrower_name: string;
  doc_category: DocCategory;
  doc_type_label: string;
  four_legs_pillar: FourLegsPillar;
  routed_seat: RoutedSeat;
  routing_reason: string;
  confidence: "high" | "medium" | "low";
  laya_family: LayaDocumentFamily;
  statement_period: string | null;
  segmentation_guard_triggered: boolean;
  extra?: Record<string, string | number | boolean | null>;
}): FileClassificationPreview {
  const diag = extractPreUnderwritingDiagnostics(
    input.filename,
    input.rawCombined ?? input.filename.toLowerCase(),
    input.doc_category,
    input.segmentation_guard_triggered,
  );
  const structuralFeatures = extractLayaStructuralFeatures(
    `${input.filename}\n${input.rawCombined ?? ""}`,
  );
  const emailTriage = extractEmailTriage(input.filename, input.rawCombined);
  const documentCompleteness = extractDocumentCompletenessDiagnostics(
    input.filename,
    input.rawCombined,
    { status: diag.page_integrity, advisory: diag.missing_pages_summary },
  );
  const highlights: SmartDropExtractedHighlights = {
    laya_family: input.laya_family,
    statement_period: input.statement_period,
    segmentation_guard_triggered: input.segmentation_guard_triggered,
    model_checkpoint: LAYA_ARCHIVE_C6_CHECKPOINT,
    structural_features: structuralFeatures,
    document_completeness: documentCompleteness,
    intake_warnings: documentCompleteness.warnings,
    ...(emailTriage ? { email_triage: emailTriage } : {}),
    source_filename: input.filename,
    account_hint: diag.account_last4 ? `#${diag.account_last4}` : null,
    page_integrity: diag.page_integrity,
    missing_pages_summary: diag.missing_pages_summary,
    account_last4: diag.account_last4,
    institution_name: diag.institution_name,
    proactive_loe_kind: diag.proactive_loe_kind,
    native_signature_seal: diag.native_signature_seal,
    ...(input.extra ?? {}),
  };
  const facts: Record<string, string | number | boolean | null> = {
    source_filename: input.filename,
    laya_family: input.laya_family,
    statement_period: input.statement_period,
    segmentation_guard_triggered: input.segmentation_guard_triggered,
    model_checkpoint: LAYA_ARCHIVE_C3_CHECKPOINT,
    page_integrity: diag.page_integrity,
    missing_pages_summary: diag.missing_pages_summary,
    account_last4: diag.account_last4,
    institution_name: diag.institution_name,
    proactive_loe_kind: diag.proactive_loe_kind,
    native_signature_seal: diag.native_signature_seal,
    ...(input.extra ?? {}),
  };
  return {
    detected_borrower_name: input.detected_borrower_name,
    doc_category: input.doc_category,
    doc_type_label: input.doc_type_label,
    four_legs_pillar: input.four_legs_pillar,
    routed_seat: input.routed_seat,
    routing_reason: input.routing_reason,
    confidence: input.confidence,
    laya_family: input.laya_family,
    statement_period: input.statement_period,
    segmentation_guard_triggered: input.segmentation_guard_triggered,
    page_integrity: diag.page_integrity,
    missing_pages_summary: diag.missing_pages_summary,
    account_last4: diag.account_last4,
    institution_name: diag.institution_name,
    proactive_loe_kind: diag.proactive_loe_kind,
    native_signature_seal: diag.native_signature_seal,
    extracted_highlights: highlights,
    extracted_facts: facts,
  };
}

export function previewFileClassification(
  fileOrName:
    | string
    | {
        name: string;
        type?: string;
        size?: number;
        content?: string;
        textPreview?: string;
      },
  contextOrExcerpt:
    | string
    | null
    | {
        borrowerName?: string | null;
        conditionId?: string | null;
        uploaderRole?: UploaderRole;
        contentExcerpt?: string | null;
      } = {},
  borrowerHint?: string | null,
): FileClassificationPreview {
  const context =
    typeof contextOrExcerpt === "string" || contextOrExcerpt === null
      ? {
          borrowerName: borrowerHint ?? undefined,
          contentExcerpt: contextOrExcerpt ?? undefined,
        }
      : {
          ...contextOrExcerpt,
          borrowerName: contextOrExcerpt.borrowerName ?? borrowerHint ?? undefined,
        };
  const filename =
    typeof fileOrName === "string"
      ? fileOrName
      : (fileOrName?.name ?? "document.pdf");
  const lower = filename.toLowerCase();
  const contentPreview =
    typeof fileOrName === "object"
      ? (fileOrName.content ?? fileOrName.textPreview ?? context.contentExcerpt ?? "")
      : (context.contentExcerpt ?? "");
  const rawCombined = `${lower} ${contentPreview.slice(0, 500).toLowerCase()}`;
  const combined = `${rawCombined} ${rawCombined.replace(/[_.]+/g, " ")}`;
  const makePreviewResult = (
    input: Omit<Parameters<typeof buildPreviewResult>[0], "rawCombined">,
  ) => buildPreviewResult({ ...input, rawCombined });

  const detected_borrower_name = extractBorrowerFromFilename(
    filename,
    context.borrowerName,
    contentPreview,
  );
  const statement_period = extractStatementPeriod(rawCombined);
  const isMixedPage =
    /\b(mixed_page|mixed page|mixed-page|segmentation_hold|segmentation hold|multi_doc_page|unsplit_bundle)\b/.test(
      combined,
    );

  // 0. Mixed-page segmentation guard check (Laya 16-class `mixed_page`)
  if (isMixedPage) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "other",
      doc_type_label: "Mixed-Page Packet (Segmentation Hold)",
      four_legs_pillar: "Conditions",
      routed_seat: "Processor",
      routing_reason:
        "Mixed-page document detected by Laya segmentation guard; hold for page split before underwriting",
      confidence: "high",
      laya_family: "mixed_page",
      statement_period,
      segmentation_guard_triggered: true,
    });
  }

  // 1. Condition Clearing / LOE / Stips (or explicit conditionId scope)
  if (
    context.conditionId ||
    /\b(condition|stip|stips|loe|explanation|clearing|4506|exception_memo)\b/.test(
      combined,
    )
  ) {
    const family: LayaDocumentFamily = /\b(utility|water_bill|electric|pge)\b/.test(
      combined,
    )
      ? "utility_bill"
      : /\b(mortgage_stmt|mortgage_statement)\b/.test(combined)
        ? "mortgage_statement"
        : /\b(invoice|receipt)\b/.test(combined)
          ? "invoice"
          : "other";
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "condition_clearing",
      doc_type_label: DOC_CATEGORY_LABELS.condition_clearing,
      four_legs_pillar: "Conditions",
      routed_seat: "AM",
      routing_reason:
        "Underwriting condition clearing & stip evidence review (Account Manager)",
      confidence: "high",
      laya_family: family,
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 2. 1003 / MISMO XML / Loan Application -> AE
  if (
    lower.endsWith(".xml") ||
    lower.endsWith(".fnm") ||
    /\b(1003|mismo|urla|application|prequal|scenario_intake)\b/.test(combined)
  ) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "mismo_1003",
      doc_type_label: DOC_CATEGORY_LABELS.mismo_1003,
      four_legs_pillar: "Application",
      routed_seat: "AE",
      routing_reason:
        "Pre-Qual & 1003 scenario intake for Four-Legs Desk (Account Executive)",
      confidence: "high",
      laya_family: "application",
      statement_period,
      segmentation_guard_triggered: false,
      extra: {
        format: lower.endsWith(".xml") ? "MISMO 3.4 XML" : "1003 Package",
      },
    });
  }

  // 3. Bank Statements -> CA (Income)
  if (
    /\b(bank|stmt|estmt|statements?|12mo|24mo|12-mo|24-mo|chase|wells|bofa|deposits)\b/.test(
      combined,
    ) &&
    !/\b(mortgage_statement|mortgage_stmt|mtg\s+stmt|w2stmt|taxstatement|credit_card)\b/.test(
      combined,
    )
  ) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "bank_statements",
      doc_type_label: DOC_CATEGORY_LABELS.bank_statements,
      four_legs_pillar: "Income",
      routed_seat: "CA",
      routing_reason:
        "Four-Legs Income deposit & cash-flow underwriting (Credit Analyst)",
      confidence: "high",
      laya_family: "bank_statement",
      statement_period: statement_period ?? "12Mo Statement Window",
      segmentation_guard_triggered: false,
    });
  }

  // 4. Tax Returns / CPA / W-2 / 1099 / Paystub / P&L -> CA (Income)
  if (
    /\b(tax|1040|w2|w-2|1099|k1|k-1|pnl|p&l|profit|cpa|seq|paystub|pay_stub|income)\b/.test(
      combined,
    )
  ) {
    const family: LayaDocumentFamily = /\b(paystub|pay_stub)\b/.test(combined)
      ? "paystub"
      : /\b(w2|w-2|1099|k1|k-1)\b/.test(combined)
        ? "tax_form"
        : "tax_return";
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "tax_returns",
      doc_type_label: DOC_CATEGORY_LABELS.tax_returns,
      four_legs_pillar: "Income",
      routed_seat: "CA",
      routing_reason:
        "Four-Legs Income qualification & tax/CPA analysis (Credit Analyst)",
      confidence: "high",
      laya_family: family,
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 5. Credit Report / Mortgage Statement / Credit Card Statement -> CA (Credit)
  if (
    /\b(credit|fico|trimerge|tri-merge|experian|equifax|transunion|tradeline|mortgage_statement|mortgage_stmt|credit_card)\b/.test(
      combined,
    )
  ) {
    const family: LayaDocumentFamily = /\b(mortgage_statement|mortgage_stmt)\b/.test(
      combined,
    )
      ? "mortgage_statement"
      : /\b(credit_card)\b/.test(combined)
        ? "credit_card_statement"
        : "credit_report";
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "credit_report",
      doc_type_label: DOC_CATEGORY_LABELS.credit_report,
      four_legs_pillar: "Credit",
      routed_seat: "CA",
      routing_reason:
        "Four-Legs Credit depth, housing history & liabilities review (Credit Analyst)",
      confidence: "high",
      laya_family: family,
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 6. Asset / Reserve Statements -> CA (Assets)
  if (
    /\b(asset|assets|reserve|reserves|fidelity|schwab|vanguard|vod|earnest|emd|gift|liquidity|brokerage_stmt)\b/.test(
      combined,
    )
  ) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "asset_statements",
      doc_type_label: DOC_CATEGORY_LABELS.asset_statements,
      four_legs_pillar: "Assets",
      routed_seat: "CA",
      routing_reason:
        "Four-Legs Assets, cash-to-close & reserves verification (Credit Analyst)",
      confidence: "high",
      laya_family: "bank_statement",
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 7. Appraisal / Valuation -> Processor (Equity)
  if (/\b(appraisal|1004|1007|bpo|cda|valuation|collateral)\b/.test(combined)) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "appraisal_valuation",
      doc_type_label: DOC_CATEGORY_LABELS.appraisal_valuation,
      four_legs_pillar: "Equity",
      routed_seat: "Processor",
      routing_reason:
        "Property appraisal, collateral & LTV/CLTV packaging (Processor)",
      confidence: "high",
      laya_family: "appraisal",
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 8. Lease / Rent Roll -> Processor (Income)
  if (
    /\b(lease|rent_roll|rent-roll|rentroll|str|airbnb|vrbo)\b/.test(combined)
  ) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "lease_rent_roll",
      doc_type_label: DOC_CATEGORY_LABELS.lease_rent_roll,
      four_legs_pillar: "Income",
      routed_seat: "Processor",
      routing_reason:
        "Subject property lease & DSCR rent schedule verification (Processor)",
      confidence: "high",
      laya_family: "lease",
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 9. Title / Escrow -> Processor (Equity)
  if (
    /\b(title|prelim|escrow|cpl|payoff|alta|settlement|wire|hud)\b/.test(
      combined,
    )
  ) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "title_escrow",
      doc_type_label: DOC_CATEGORY_LABELS.title_escrow,
      four_legs_pillar: "Equity",
      routed_seat: "Processor",
      routing_reason:
        "Title commitment, vesting & lien payoff packaging (Processor)",
      confidence: "high",
      laya_family: "other",
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 10. Hazard / Flood Insurance (HOI) -> Processor (Equity)
  if (
    /\b(hoi|hazard|insurance|flood|binder|dec_page|acord|evidence_of_insurance)\b/.test(
      combined,
    )
  ) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "insurance_hoi",
      doc_type_label: DOC_CATEGORY_LABELS.insurance_hoi,
      four_legs_pillar: "Equity",
      routed_seat: "Processor",
      routing_reason:
        "Property hazard/flood insurance & mortgagee clause packaging (Processor)",
      confidence: "high",
      laya_family: "insurance",
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 11. Entity / LLC Docs -> Processor (Application)
  if (
    /\b(llc|operating_agreement|operating|articles|ein|entity|vesting|good_standing|bylaws|trust)\b/.test(
      combined,
    )
  ) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "entity_docs",
      doc_type_label: DOC_CATEGORY_LABELS.entity_docs,
      four_legs_pillar: "Application",
      routed_seat: "Processor",
      routing_reason:
        "Entity formation, EIN & signer authority packaging (Processor)",
      confidence: "high",
      laya_family: "other",
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 12. Purchase Contract -> Processor (Equity)
  if (/\b(purchase|psa|contract|addendum|sales_contract)\b/.test(combined)) {
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "purchase_contract",
      doc_type_label: DOC_CATEGORY_LABELS.purchase_contract,
      four_legs_pillar: "Equity",
      routed_seat: "Processor",
      routing_reason:
        "Executed purchase contract & transaction terms review (Processor)",
      confidence: "high",
      laya_family: "other",
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  // 13. Disclosures / LE / CD / Invoice / Utility -> AM (Conditions)
  if (
    /\b(disclosure|disclosures|loan_estimate|closing_disclosure|intent_to_proceed|fee_sheet|utility|invoice)\b/.test(
      combined,
    )
  ) {
    const family: LayaDocumentFamily = /\b(utility)\b/.test(combined)
      ? "utility_bill"
      : /\b(invoice)\b/.test(combined)
        ? "invoice"
        : "other";
    return makePreviewResult({
      filename,
      detected_borrower_name,
      doc_category: "disclosures_le_cd",
      doc_type_label: DOC_CATEGORY_LABELS.disclosures_le_cd,
      four_legs_pillar: "Conditions",
      routed_seat: "AM",
      routing_reason:
        "Disclosure acknowledgment & condition clearing (Account Manager)",
      confidence: "medium",
      laya_family: family,
      statement_period,
      segmentation_guard_triggered: false,
    });
  }

  return makePreviewResult({
    filename,
    detected_borrower_name,
    doc_category: "other",
    doc_type_label: DOC_CATEGORY_LABELS.other,
    four_legs_pillar: "Application",
    routed_seat: "AE",
    routing_reason:
      "Initial document intake & borrower triage (Account Executive)",
    confidence: "medium",
    laya_family: "other",
    statement_period,
    segmentation_guard_triggered: false,
  });
}

export function groupItemsByBorrower(
  items: readonly SmartDropItem[],
): BorrowerDropGroup[] {
  const map = new Map<string, BorrowerDropGroup>();
  for (const item of items) {
    const borrowerName =
      item.detected_borrower_name?.trim() ||
      item.borrower_name?.trim() ||
      "Unassigned Borrower (Pending Match)";
    const key = borrowerName.toLowerCase();
    const existing = map.get(key);
    if (!existing) {
      const pillars = [item.four_legs_pillar];
      const seats = [item.routed_seat];
      map.set(key, {
        borrower_key: key,
        borrowerKey: key,
        borrower_name: borrowerName,
        borrowerName,
        detected_borrower_name: borrowerName,
        lo_name: item.lo_name ?? null,
        company_name: item.company_name ?? null,
        person_id: item.person_id ?? null,
        loan_id: item.loan_id ?? null,
        items: [item],
        pillars,
        fourLegsPillars: pillars,
        seats,
        routedSeats: seats,
        latest_uploaded_at: item.uploaded_at || item.created_at || "",
      });
    } else {
      existing.items.push(item);
      if (!existing.lo_name && item.lo_name) existing.lo_name = item.lo_name;
      if (!existing.company_name && item.company_name)
        existing.company_name = item.company_name;
      if (!existing.person_id && item.person_id)
        existing.person_id = item.person_id;
      if (!existing.loan_id && item.loan_id) existing.loan_id = item.loan_id;
      if (!existing.pillars.includes(item.four_legs_pillar)) {
        existing.pillars.push(item.four_legs_pillar);
      }
      if (!existing.seats.includes(item.routed_seat)) {
        existing.seats.push(item.routed_seat);
      }
      const itemUploadedAt = item.uploaded_at || item.created_at || "";
      if (itemUploadedAt > (existing.latest_uploaded_at || "")) {
        existing.latest_uploaded_at = itemUploadedAt;
      }
    }
  }
  return [...map.values()].sort((a, b) =>
    (b.latest_uploaded_at || "").localeCompare(a.latest_uploaded_at || ""),
  );
}

function deterministicHexHash(seed: string): string {
  let h1 = 0xdeadbeef ^ seed.length;
  let h2 = 0x41c6ce57 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    const ch = seed.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const part =
    (h1 >>> 0).toString(16).padStart(8, "0") +
    (h2 >>> 0).toString(16).padStart(8, "0");
  return part.repeat(4).slice(0, 64);
}

export async function fileToUploadPayload(
  file:
    | File
    | {
        name: string;
        type?: string;
        size?: number;
        content?: string;
        arrayBuffer?: () => Promise<ArrayBuffer>;
      },
  context: {
    borrowerName?: string | null;
    conditionId?: string | null;
    uploaderRole?: UploaderRole;
  } = {},
): Promise<SmartDropUploadPayload> {
  const filename = file.name || "document.pdf";
  const mime_type =
    file.type ||
    (filename.endsWith(".xml") ? "application/xml" : "application/pdf");
  const size_bytes =
    typeof file.size === "number"
      ? file.size
      : "content" in file && typeof file.content === "string"
        ? file.content.length
        : 24576;

  let sha256 = "";
  try {
    if (
      typeof file.arrayBuffer === "function" &&
      globalThis.crypto?.subtle?.digest
    ) {
      const buf = await file.arrayBuffer();
      const digest = await globalThis.crypto.subtle.digest("SHA-256", buf);
      sha256 = Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }
  } catch {
    // Fallback to deterministic hash when running in a test/jsdom environment
  }
  if (!sha256) {
    sha256 = deterministicHexHash(`${filename}:${size_bytes}:${mime_type}`);
  }

  const classification = previewFileClassification(file, context);
  return {
    filename,
    file_name: filename,
    mime_type,
    size_bytes,
    byte_size: size_bytes,
    sha256,
    detected_borrower_name: classification.detected_borrower_name,
    doc_category: classification.doc_category,
    doc_type_label: classification.doc_type_label,
    four_legs_pillar: classification.four_legs_pillar,
    routed_seat: classification.routed_seat,
    routing_reason: classification.routing_reason,
    laya_family: classification.laya_family,
    statement_period: classification.statement_period,
    segmentation_guard_triggered: classification.segmentation_guard_triggered,
    extracted_highlights: classification.extracted_highlights,
    extracted_facts: classification.extracted_facts,
  };
}

function normalizeFourLegsPillarValue(
  raw: string | undefined | null,
  fallback: FourLegsPillar,
): FourLegsPillar {
  if (!raw) return fallback;
  const lower = raw.trim().toLowerCase();
  if (lower === "income") return "Income";
  if (lower === "credit") return "Credit";
  if (lower === "equity") return "Equity";
  if (lower === "assets") return "Assets";
  if (lower === "conditions") return "Conditions";
  if (lower === "application") return "Application";
  return fallback;
}

function normalizeReviewStatusValue(
  raw: string | undefined | null,
  fallback: ReviewStatus,
): ReviewStatus {
  if (!raw) return fallback;
  const lower = raw.trim().toLowerCase();
  if (lower === "pending_review" || lower === "pending_triage" || lower === "pending") {
    return "pending_triage";
  }
  if (lower === "needs_review") return "needs_review";
  if (lower === "accepted") return "accepted";
  if (lower === "bound_to_loan") return "bound_to_loan";
  if (lower === "attached_to_condition") return "attached_to_condition";
  if (lower === "archived") return "archived";
  if (lower === "rejected") return "rejected";
  return fallback;
}

export function normalizeSmartDropItem(raw: SmartDropItem): SmartDropItem {
  const rawRecord = raw as unknown as Record<string, unknown>;
  const filename = raw.filename || raw.file_name || "document.pdf";
  const borrower =
    raw.detected_borrower_name ||
    raw.borrower_name ||
    "Unassigned Borrower (Pending Match)";
  const condition_id =
    raw.condition_id ??
    (typeof rawRecord.matched_condition_id === "string"
      ? rawRecord.matched_condition_id
      : null);
  const preview = previewFileClassification(filename, {
    borrowerName: borrower,
    conditionId: condition_id,
    uploaderRole: raw.uploader_role,
  });
  const rawHighlights: SmartDropExtractedHighlights = {
    ...(raw.extracted_facts ?? {}),
    ...(raw.extracted_highlights ?? {}),
  };
  const highlights: SmartDropExtractedHighlights = {
    ...preview.extracted_highlights,
    ...rawHighlights,
  };
  const laya_family =
    raw.laya_family ??
    (typeof rawHighlights.laya_family === "string"
      ? rawHighlights.laya_family
      : undefined) ??
    preview.laya_family;

  const rawPeriod = raw.statement_period ?? rawHighlights.statement_period;
  let statement_period: string | null = preview.statement_period;
  if (typeof rawPeriod === "string") {
    statement_period = rawPeriod;
  } else if (rawPeriod === null) {
    statement_period = null;
  } else if (rawPeriod && typeof rawPeriod === "object") {
    const pObj = rawPeriod as { status?: string; start?: string; end?: string };
    if (pObj.status === "source_extracted" && pObj.start && pObj.end) {
      statement_period = `${pObj.start} to ${pObj.end}`;
    } else if (pObj.start) {
      statement_period = pObj.start;
    }
  }

  const segmentation_guard_triggered = Boolean(
    raw.segmentation_guard_triggered ??
      rawHighlights.segmentation_guard_triggered ??
      (laya_family === "mixed_page" || preview.segmentation_guard_triggered),
  );

  const rawPi = raw.page_integrity ?? rawHighlights.page_integrity;
  let page_integrity: PageIntegrityStatus = preview.page_integrity;
  let piAdvisory: string | null = null;
  if (typeof rawPi === "string") {
    page_integrity = rawPi as PageIntegrityStatus;
  } else if (rawPi && typeof rawPi === "object") {
    const piObj = rawPi as {
      status?: PageIntegrityStatus;
      advisory?: string | null;
      missing_pages?: number[];
    };
    if (piObj.status) page_integrity = piObj.status;
    if (typeof piObj.advisory === "string" && piObj.advisory.trim()) {
      piAdvisory = piObj.advisory.trim();
    } else if (Array.isArray(piObj.missing_pages) && piObj.missing_pages.length > 0) {
      piAdvisory = `Missing page(s): ${piObj.missing_pages.join(", ")}`;
    }
  }

  const missing_pages_summary =
    raw.missing_pages_summary !== undefined
      ? raw.missing_pages_summary
      : typeof rawHighlights.missing_pages_summary === "string"
        ? rawHighlights.missing_pages_summary
        : piAdvisory ?? preview.missing_pages_summary;

  const continuityObj =
    rawHighlights.statement_continuity &&
    typeof rawHighlights.statement_continuity === "object"
      ? (rawHighlights.statement_continuity as {
          account_last4?: string | null;
          institution?: string | null;
        })
      : null;

  const account_last4 =
    raw.account_last4 !== undefined
      ? raw.account_last4
      : typeof rawHighlights.account_last4 === "string"
        ? rawHighlights.account_last4
        : (typeof continuityObj?.account_last4 === "string"
            ? continuityObj.account_last4
            : preview.account_last4);
  const institution_name =
    raw.institution_name !== undefined
      ? raw.institution_name
      : typeof rawHighlights.institution_name === "string"
        ? rawHighlights.institution_name
        : (typeof continuityObj?.institution === "string"
            ? continuityObj.institution
            : preview.institution_name);

  let backendAdvisoryKind: string | null = null;
  if (Array.isArray(rawHighlights.proactive_advisories)) {
    for (const adv of rawHighlights.proactive_advisories as { code?: string }[]) {
      if (adv?.code === "deposit_source_loe") {
        backendAdvisoryKind = "loe_large_deposit";
        break;
      }
      if (adv?.code === "credit_inquiry_loe") {
        backendAdvisoryKind = "loe_credit_inquiry";
        break;
      }
    }
  }

  const proactive_loe_kind =
    raw.proactive_loe_kind !== undefined
      ? raw.proactive_loe_kind
      : typeof rawHighlights.proactive_loe_kind === "string"
        ? rawHighlights.proactive_loe_kind
        : backendAdvisoryKind ?? preview.proactive_loe_kind;

  const sigObj =
    rawHighlights.ls2_native_signature &&
    typeof rawHighlights.ls2_native_signature === "object"
      ? (rawHighlights.ls2_native_signature as {
          signature_seal_sha256?: string;
          seal_sha256?: string;
        })
      : null;

  const native_signature_seal =
    raw.native_signature_seal ??
    (typeof rawHighlights.native_signature_seal === "string"
      ? rawHighlights.native_signature_seal
      : typeof rawHighlights.native_esign_seal === "string"
        ? rawHighlights.native_esign_seal
        : typeof rawHighlights.ls2_native_signature === "string"
          ? rawHighlights.ls2_native_signature
          : sigObj?.signature_seal_sha256 ??
            sigObj?.seal_sha256 ??
            preview.native_signature_seal);

  const uploaded_at =
    raw.uploaded_at ||
    raw.created_at ||
    new Date().toISOString();
  const size_bytes =
    typeof raw.size_bytes === "number"
      ? raw.size_bytes
      : typeof rawRecord.byte_size === "number"
        ? rawRecord.byte_size
        : 0;
  const uploader_name =
    raw.uploader_name ??
    (typeof rawRecord.uploader_label === "string" ? rawRecord.uploader_label : null);
  const notes =
    raw.notes ??
    (typeof rawRecord.review_note === "string" ? rawRecord.review_note : null);
  const four_legs_pillar = normalizeFourLegsPillarValue(
    raw.four_legs_pillar,
    preview.four_legs_pillar,
  );
  const review_status = normalizeReviewStatusValue(
    raw.review_status,
    "pending_triage",
  );

  return {
    ...raw,
    filename,
    file_name: raw.file_name || filename,
    size_bytes,
    uploaded_at,
    uploader_name,
    condition_id,
    notes,
    four_legs_pillar,
    review_status,
    detected_borrower_name: borrower,
    borrower_name: raw.borrower_name || borrower,
    laya_family,
    statement_period,
    segmentation_guard_triggered,
    page_integrity,
    missing_pages_summary,
    account_last4,
    institution_name,
    proactive_loe_kind,
    native_signature_seal,
    extracted_highlights: {
      ...highlights,
      laya_family,
      statement_period,
      segmentation_guard_triggered,
      page_integrity,
      missing_pages_summary,
      account_last4,
      institution_name,
      proactive_loe_kind,
      native_signature_seal,
    },
  };
}

function makeLocalLink(input: {
  portal_role: PortalRole;
  recipient_email?: string | null;
  person_id?: string | null;
  company_id?: string | null;
  lo_name?: string | null;
  company_name?: string | null;
  deal_link_id?: string | null;
  loan_id?: string | null;
  condition_id?: string | null;
  borrower_name?: string | null;
  label?: string | null;
}): SmartDropLink {
  const slug = deterministicHexHash(
    `${input.portal_role}:${input.person_id ?? ""}:${input.loan_id ?? ""}:${input.condition_id ?? ""}:${input.lo_name ?? ""}:${input.recipient_email ?? ""}`,
  ).slice(0, 16);
  const token = `lsdrop_${slug}`;
  const existing = memoryLinks.get(token);
  if (existing && !existing.revoked_at) {
    return existing;
  }
  const link: SmartDropLink = {
    id: `sdlink_${idCounter++}`,
    token,
    portal_role: input.portal_role,
    recipient_email: input.recipient_email ?? null,
    person_id: input.person_id ?? null,
    company_id: input.company_id ?? null,
    lo_name: input.lo_name ?? null,
    company_name: input.company_name ?? null,
    deal_link_id: input.deal_link_id ?? null,
    loan_id: input.loan_id ?? null,
    condition_id: input.condition_id ?? null,
    borrower_name: input.borrower_name ?? null,
    label: input.label ?? null,
    created_at: new Date().toISOString(),
    expires_at: null,
    revoked_at: null,
    upload_count: 0,
    url: buildSmartDropUrl(token),
  };
  memoryLinks.set(token, link);
  return link;
}

export function getCachedSmartDropItems(params?: {
  person_id?: string | null;
  company_id?: string | null;
  loan_id?: string | null;
  condition_id?: string | null;
  routed_seat?: RoutedSeat | null;
}): SmartDropItem[] {
  return filterSmartDropItems([...memoryItems.values()], params);
}

function filterSmartDropItems(
  items: readonly SmartDropItem[],
  params?: {
    person_id?: string | null;
    company_id?: string | null;
    loan_id?: string | null;
    condition_id?: string | null;
    routed_seat?: RoutedSeat | null;
    four_legs_pillar?: FourLegsPillar | null;
    review_status?: ReviewStatus | null;
  },
): SmartDropItem[] {
  return items
    .filter((item) => {
      if (params?.person_id && item.person_id !== params.person_id) return false;
      if (params?.company_id && item.company_id !== params.company_id)
        return false;
      if (params?.loan_id && item.loan_id !== params.loan_id) return false;
      if (params?.condition_id && item.condition_id !== params.condition_id)
        return false;
      if (params?.routed_seat && item.routed_seat !== params.routed_seat)
        return false;
      if (
        params?.four_legs_pillar &&
        item.four_legs_pillar !== params.four_legs_pillar
      )
        return false;
      if (params?.review_status && item.review_status !== params.review_status)
        return false;
      return true;
    })
    .sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));
}

function buildInboxSummary(items: SmartDropItem[]): SmartDropInboxResponse {
  const counts_by_seat: Record<RoutedSeat, number> = {
    AE: 0,
    CA: 0,
    AM: 0,
    Processor: 0,
  };
  const counts_by_pillar: Record<FourLegsPillar, number> = {
    Income: 0,
    Credit: 0,
    Equity: 0,
    Assets: 0,
    Application: 0,
    Conditions: 0,
  };
  for (const item of items) {
    if (item.routed_seat in counts_by_seat) {
      counts_by_seat[item.routed_seat] += 1;
    }
    if (item.four_legs_pillar in counts_by_pillar) {
      counts_by_pillar[item.four_legs_pillar] += 1;
    }
  }
  return {
    items,
    links: [...memoryLinks.values()].filter((l) => !l.revoked_at),
    counts_by_seat,
    counts_by_pillar,
    total: items.length,
  };
}

export async function createSmartDropLink(input: {
  portal_role: PortalRole;
  recipient_email?: string | null;
  person_id?: string | null;
  company_id?: string | null;
  lo_name?: string | null;
  company_name?: string | null;
  deal_link_id?: string | null;
  loan_id?: string | null;
  condition_id?: string | null;
  borrower_name?: string | null;
  label?: string | null;
}): Promise<SmartDropLink> {
  try {
    const res = await api<
      | SmartDropLink
      | {
          link?: SmartDropLink;
          token?: string;
          token_cleartext?: string;
          id?: string;
        }
    >("/api/smart-drops/links", {
      method: "POST",
      body: input,
    });
    const candidate =
      res && typeof res === "object" && "link" in res && res.link
        ? res.link
        : (res as (SmartDropLink & { token_cleartext?: string }) | undefined);
    const resolvedToken =
      candidate?.token ||
      (candidate && "token_cleartext" in candidate
        ? candidate.token_cleartext
        : undefined);
    if (candidate && typeof resolvedToken === "string" && resolvedToken.length > 0) {
      const normalized: SmartDropLink = {
        ...candidate,
        id: candidate.id || `sdlink_${idCounter++}`,
        token: resolvedToken,
        portal_role: candidate.portal_role || input.portal_role,
        recipient_email: candidate.recipient_email ?? input.recipient_email ?? null,
        person_id: candidate.person_id ?? input.person_id ?? null,
        company_id: candidate.company_id ?? input.company_id ?? null,
        lo_name: candidate.lo_name ?? input.lo_name ?? null,
        company_name: candidate.company_name ?? input.company_name ?? null,
        deal_link_id: candidate.deal_link_id ?? input.deal_link_id ?? null,
        loan_id: candidate.loan_id ?? input.loan_id ?? null,
        condition_id: candidate.condition_id ?? input.condition_id ?? null,
        borrower_name: candidate.borrower_name ?? input.borrower_name ?? null,
        label: candidate.label ?? input.label ?? null,
        created_at: candidate.created_at || new Date().toISOString(),
        expires_at: candidate.expires_at ?? null,
        revoked_at: candidate.revoked_at ?? null,
        upload_count: candidate.upload_count ?? 0,
        url: candidate.url || buildSmartDropUrl(resolvedToken),
      };
      memoryLinks.set(normalized.token, normalized);
      return normalized;
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400) {
      throw err;
    }
    // Use local link when API endpoint is not yet mounted or in offline test mode
  }
  return makeLocalLink(input);
}

export const DEFAULT_PIPELINE_LOAN_OPTIONS: PipelineLoanOption[] = [
  {
    id: "570116419",
    loan_number: "570116419",
    borrower_name: "Elena Vance",
    lo_name: "Danushiaazhiniajar S.",
    company_name: "Pacific Crest Mortgage",
    stage: "Underwriting",
  },
  {
    id: "570116422",
    loan_number: "570116422",
    borrower_name: "Marcus Thorne",
    lo_name: "Danushiaazhiniajar S.",
    company_name: "Pacific Crest Mortgage",
    stage: "Processing",
  },
  {
    id: "570116438",
    loan_number: "570116438",
    borrower_name: "Sierra Capital Holdings LLC",
    lo_name: "Rowan Example",
    company_name: "Harbor Lending",
    stage: "Approved w/ Conditions",
  },
];

export async function fetchPipelineLoanOptions(): Promise<PipelineLoanOption[]> {
  try {
    const res = await api<
      { loans?: Record<string, unknown>[] } | Record<string, unknown>[]
    >("/api/loans");
    const rawList = Array.isArray(res)
      ? res
      : res && typeof res === "object" && Array.isArray(res.loans)
        ? res.loans
        : [];
    if (rawList.length > 0) {
      const mapped: PipelineLoanOption[] = rawList
        .slice(0, 50)
        .map((row) => ({
          id: String(row.id ?? row.loan_id ?? row.loan_number ?? ""),
          loan_number: String(
            row.loan_number ?? row.id ?? row.loan_id ?? "",
          ),
          borrower_name: String(
            row.borrower_name ?? row.borrower ?? "Pipeline Borrower",
          ),
          lo_name:
            typeof row.lo_name === "string"
              ? row.lo_name
              : typeof row.broker === "string"
                ? row.broker
                : null,
          company_name:
            typeof row.company_name === "string" ? row.company_name : null,
          stage: typeof row.stage === "string" ? row.stage : null,
          amount: typeof row.amount === "number" ? row.amount : null,
        }))
        .filter((o) => o.id.length > 0);
      if (mapped.length > 0) {
        return mapped;
      }
    }
  } catch {
    // Fallback to default pipeline loan options when offline or in unit tests
  }
  return DEFAULT_PIPELINE_LOAN_OPTIONS;
}

export async function listSmartDropLinks(params?: {
  person_id?: string;
  company_id?: string;
  loan_id?: string;
  condition_id?: string;
  portal_role?: PortalRole;
}): Promise<SmartDropLink[]> {
  try {
    const q = new URLSearchParams();
    if (params?.person_id) q.set("person_id", params.person_id);
    if (params?.company_id) q.set("company_id", params.company_id);
    if (params?.loan_id) q.set("loan_id", params.loan_id);
    if (params?.condition_id) q.set("condition_id", params.condition_id);
    if (params?.portal_role) q.set("portal_role", params.portal_role);
    const suffix = q.toString() ? `?${q.toString()}` : "";
    const res = await api<SmartDropLink[] | { links?: SmartDropLink[] }>(
      `/api/smart-drops/links${suffix}`,
    );
    const list = Array.isArray(res)
      ? res
      : Array.isArray(res?.links)
        ? res.links
        : [];
    for (const rawLink of list) {
      const resolvedToken =
        rawLink?.token ||
        (rawLink && "token_cleartext" in rawLink
          ? (rawLink as SmartDropLink & { token_cleartext?: string }).token_cleartext
          : undefined);
      if (rawLink && resolvedToken) {
        const normalized: SmartDropLink = {
          ...rawLink,
          token: resolvedToken,
          url: rawLink.url || buildSmartDropUrl(resolvedToken),
        };
        memoryLinks.set(resolvedToken, normalized);
      }
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400) {
      throw err;
    }
    // Fallback to memoryLinks
  }
  return [...memoryLinks.values()].filter((l) => {
    if (l.revoked_at) return false;
    if (params?.person_id && l.person_id !== params.person_id) return false;
    if (params?.company_id && l.company_id !== params.company_id) return false;
    if (params?.loan_id && l.loan_id !== params.loan_id) return false;
    if (params?.condition_id && l.condition_id !== params.condition_id)
      return false;
    if (params?.portal_role && l.portal_role !== params.portal_role)
      return false;
    return true;
  });
}

export async function revokeSmartDropLink(
  idOrToken: string,
): Promise<{ revoked: boolean }> {
  let targetId = idOrToken;
  for (const [token, link] of memoryLinks.entries()) {
    if (token === idOrToken || link.id === idOrToken) {
      if (link.id && !link.id.startsWith("sdlink_")) {
        targetId = link.id;
      }
      break;
    }
  }
  try {
    await api(`/api/smart-drops/links/${encodeURIComponent(targetId)}`, {
      method: "DELETE",
    });
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400) {
      throw err;
    }
    // Revoked in local memory when offline
  }
  for (const [token, link] of memoryLinks.entries()) {
    if (token === idOrToken || link.id === idOrToken) {
      memoryLinks.set(token, {
        ...link,
        revoked_at: new Date().toISOString(),
      });
    }
  }
  return { revoked: true };
}

export async function fetchPublicDropPortal(
  token: string,
): Promise<PublicDropPortalInfo> {
  try {
    const res = await api<PublicDropPortalInfo>(
      `/api/public/drop/${encodeURIComponent(token)}`,
    );
    if (res && typeof res === "object" && (res.link?.token || "link_id" in res)) {
      const rawItems = Array.isArray(res.recent_items)
        ? res.recent_items
        : Array.isArray(res.items)
          ? res.items
          : [];
      const items = rawItems.map(normalizeSmartDropItem);
      for (const item of items) {
        if (item?.id) memoryItems.set(item.id, item);
      }
      return {
        ...res,
        recent_items: items,
        items,
        borrower_groups: groupItemsByBorrower(items),
      };
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400 && token.trim() !== "lsdrop_demo") {
      throw err;
    }
    // Fallback to local portal info
  }
  const link =
    memoryLinks.get(token) ??
    makeLocalLink({
      portal_role: "broker_lo",
      lo_name: "Broker / LO Drop Portal",
    });
  const items = [...memoryItems.values()].filter((i) => i.token === token);
  return {
    link,
    portal_role: link.portal_role,
    lo_name: link.lo_name ?? null,
    company_name: link.company_name ?? null,
    borrower_name: link.borrower_name ?? null,
    loan_id: link.loan_id ?? null,
    condition_id: link.condition_id ?? null,
    recent_items: items,
    items,
    borrower_groups: groupItemsByBorrower(items),
  };
}

export async function uploadToPublicDropPortal(
  token: string,
  input: {
    files: (
      | File
      | { name: string; type?: string; size?: number; content?: string }
    )[];
    uploader_name?: string | null;
    uploader_email?: string | null;
    uploader_role?: UploaderRole;
    borrower_name?: string | null;
  },
): Promise<{ items: SmartDropItem[]; borrower_groups: BorrowerDropGroup[] }> {
  const link = memoryLinks.get(token);
  const payloads = await Promise.all(
    input.files.map((f) =>
      fileToUploadPayload(f, {
        borrowerName: input.borrower_name ?? link?.borrower_name,
        conditionId: link?.condition_id,
        uploaderRole: input.uploader_role ?? link?.portal_role ?? "broker_lo",
      }),
    ),
  );

  try {
    const res = await api<
      SmartDropItem[] | { items?: SmartDropItem[] }
    >(`/api/public/drop/${encodeURIComponent(token)}/upload`, {
      method: "POST",
      body: {
        uploader_name: input.uploader_name ?? null,
        uploader_email: input.uploader_email ?? null,
        uploader_role: input.uploader_role ?? link?.portal_role ?? "broker_lo",
        borrower_name: input.borrower_name ?? null,
        files: payloads,
      },
    });
    const rawItems = Array.isArray(res)
      ? res
      : Array.isArray(res?.items)
        ? res.items
        : [];
    if (rawItems.length > 0 && (rawItems[0]?.filename || rawItems[0]?.file_name)) {
      const apiItems = rawItems.map(normalizeSmartDropItem);
      for (const item of apiItems) memoryItems.set(item.id, item);
      return {
        items: apiItems,
        borrower_groups: groupItemsByBorrower(apiItems),
      };
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400 && token.trim() !== "lsdrop_demo") {
      throw err;
    }
    // Fallback to local classification creation
  }

  const created: SmartDropItem[] = payloads.map((p) => {
    const item = normalizeSmartDropItem({
      id: `sditem_${idCounter++}`,
      token,
      link_id: link?.id ?? null,
      filename: p.filename,
      file_name: p.filename,
      mime_type: p.mime_type,
      size_bytes: p.size_bytes,
      sha256: p.sha256,
      uploaded_at: new Date().toISOString(),
      uploader_role: input.uploader_role ?? link?.portal_role ?? "broker_lo",
      uploader_name: input.uploader_name ?? link?.lo_name ?? null,
      uploader_email: input.uploader_email ?? null,
      person_id: link?.person_id ?? null,
      company_id: link?.company_id ?? null,
      lo_name: link?.lo_name ?? input.uploader_name ?? null,
      company_name: link?.company_name ?? null,
      deal_link_id: link?.deal_link_id ?? null,
      loan_id: link?.loan_id ?? null,
      condition_id: link?.condition_id ?? null,
      detected_borrower_name: p.detected_borrower_name,
      borrower_name: p.detected_borrower_name,
      doc_category: p.doc_category,
      doc_type_label: p.doc_type_label,
      four_legs_pillar: p.four_legs_pillar,
      routed_seat: p.routed_seat,
      routing_reason: p.routing_reason,
      confidence: "high",
      laya_family: p.laya_family,
      statement_period: p.statement_period,
      segmentation_guard_triggered: p.segmentation_guard_triggered,
      extracted_highlights: p.extracted_highlights,
      extracted_facts: p.extracted_facts,
      review_status: link?.condition_id
        ? "attached_to_condition"
        : link?.loan_id
          ? "bound_to_loan"
          : "pending_triage",
    });
    memoryItems.set(item.id, item);
    return item;
  });

  return {
    items: created,
    borrower_groups: groupItemsByBorrower(created),
  };
}

export async function fetchSmartDropInbox(params?: {
  person_id?: string | null;
  company_id?: string | null;
  loan_id?: string | null;
  condition_id?: string | null;
  routed_seat?: RoutedSeat | null;
  four_legs_pillar?: FourLegsPillar | null;
  review_status?: ReviewStatus | null;
}): Promise<SmartDropInboxResponse> {
  try {
    const q = new URLSearchParams();
    if (params?.person_id) q.set("person_id", params.person_id);
    if (params?.company_id) q.set("company_id", params.company_id);
    if (params?.loan_id) q.set("loan_id", params.loan_id);
    if (params?.condition_id) q.set("condition_id", params.condition_id);
    if (params?.routed_seat) q.set("routed_seat", params.routed_seat);
    if (params?.four_legs_pillar)
      q.set("four_legs_pillar", params.four_legs_pillar);
    if (params?.review_status) q.set("review_status", params.review_status);
    const suffix = q.toString() ? `?${q.toString()}` : "";
    const res = await api<
      SmartDropInboxResponse | SmartDropItem[]
    >(`/api/smart-drops/inbox${suffix}`);
    const apiItems = Array.isArray(res)
      ? res
      : res && typeof res === "object" && Array.isArray(res.items)
        ? res.items
        : [];
    for (const raw of apiItems) {
      if (
        raw &&
        typeof raw.id === "string" &&
        (typeof raw.filename === "string" || typeof raw.file_name === "string")
      ) {
        const normalized = normalizeSmartDropItem(raw);
        memoryItems.set(normalized.id, normalized);
      }
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400) {
      throw err;
    }
    // Use local memory items when endpoint is unavailable
  }

  const filtered = filterSmartDropItems([...memoryItems.values()], params);
  return buildInboxSummary(filtered);
}

export const fetchSmartDropOverview = fetchSmartDropInbox;

export async function uploadStaffSmartDrop(input: {
  files: (
    | File
    | { name: string; type?: string; size?: number; content?: string }
  )[];
  uploader_role?: UploaderRole;
  uploader_name?: string | null;
  person_id?: string | null;
  company_id?: string | null;
  lo_name?: string | null;
  company_name?: string | null;
  deal_link_id?: string | null;
  loan_id?: string | null;
  condition_id?: string | null;
  borrower_name?: string | null;
}): Promise<SmartDropItem[]> {
  const role: UploaderRole = input.uploader_role ?? "AE";
  const payloads = await Promise.all(
    input.files.map((f) =>
      fileToUploadPayload(f, {
        borrowerName: input.borrower_name,
        conditionId: input.condition_id,
        uploaderRole: role,
      }),
    ),
  );

  try {
    const res = await api<SmartDropItem[] | { items?: SmartDropItem[] }>(
      "/api/smart-drops/upload",
      {
        method: "POST",
        body: {
          uploader_role: role,
          uploader_name: input.uploader_name ?? null,
          person_id: input.person_id ?? null,
          company_id: input.company_id ?? null,
          lo_name: input.lo_name ?? null,
          company_name: input.company_name ?? null,
          deal_link_id: input.deal_link_id ?? null,
          loan_id: input.loan_id ?? null,
          condition_id: input.condition_id ?? null,
          borrower_name: input.borrower_name ?? null,
          files: payloads,
        },
      },
    );
    const rawItems = Array.isArray(res)
      ? res
      : res && typeof res === "object" && Array.isArray(res.items)
        ? res.items
        : [];
    if (rawItems.length > 0 && (rawItems[0]?.filename || rawItems[0]?.file_name)) {
      const apiItems = rawItems.map(normalizeSmartDropItem);
      for (const item of apiItems) memoryItems.set(item.id, item);
      return apiItems;
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400) {
      throw err;
    }
    // Fallback to local memory item creation
  }

  const created: SmartDropItem[] = payloads.map((p) => {
    const item = normalizeSmartDropItem({
      id: `sditem_${idCounter++}`,
      token: null,
      link_id: null,
      filename: p.filename,
      file_name: p.filename,
      mime_type: p.mime_type,
      size_bytes: p.size_bytes,
      sha256: p.sha256,
      uploaded_at: new Date().toISOString(),
      uploader_role: role,
      uploader_name: input.uploader_name ?? null,
      person_id: input.person_id ?? null,
      company_id: input.company_id ?? null,
      lo_name: input.lo_name ?? null,
      company_name: input.company_name ?? null,
      deal_link_id: input.deal_link_id ?? null,
      loan_id: input.loan_id ?? null,
      condition_id: input.condition_id ?? null,
      detected_borrower_name: p.detected_borrower_name,
      borrower_name: p.detected_borrower_name,
      doc_category: p.doc_category,
      doc_type_label: p.doc_type_label,
      four_legs_pillar: p.four_legs_pillar,
      routed_seat: p.routed_seat,
      routing_reason: p.routing_reason,
      confidence: "high",
      laya_family: p.laya_family,
      statement_period: p.statement_period,
      segmentation_guard_triggered: p.segmentation_guard_triggered,
      extracted_highlights: p.extracted_highlights,
      extracted_facts: p.extracted_facts,
      review_status: input.condition_id
        ? "attached_to_condition"
        : input.loan_id
          ? "bound_to_loan"
          : "pending_triage",
    });
    memoryItems.set(item.id, item);
    return item;
  });

  return created;
}

export async function updateSmartDropItem(
  itemId: string,
  patch: Partial<
    Pick<
      SmartDropItem,
      | "loan_id"
      | "condition_id"
      | "routed_seat"
      | "doc_category"
      | "doc_type_label"
      | "four_legs_pillar"
      | "detected_borrower_name"
      | "borrower_name"
      | "review_status"
      | "notes"
      | "routing_reason"
      | "laya_family"
      | "statement_period"
      | "segmentation_guard_triggered"
      | "page_integrity"
      | "missing_pages_summary"
      | "account_last4"
      | "institution_name"
      | "proactive_loe_kind"
      | "native_signature_seal"
      | "extracted_highlights"
    >
  >,
): Promise<SmartDropItem> {
  const existing = memoryItems.get(itemId);
  const nextDocTypeLabel =
    patch.doc_type_label ??
    (patch.doc_category ? DOC_CATEGORY_LABELS[patch.doc_category] : undefined);

  const optimistic = normalizeSmartDropItem({
    ...(existing ?? {
      id: itemId,
      filename: "document.pdf",
      file_name: "document.pdf",
      mime_type: "application/pdf",
      size_bytes: 1024,
      sha256: deterministicHexHash(itemId),
      uploaded_at: new Date().toISOString(),
      uploader_role: "AE",
      detected_borrower_name: "Unassigned Borrower (Pending Match)",
      borrower_name: "Unassigned Borrower (Pending Match)",
      doc_category: "other",
      doc_type_label: DOC_CATEGORY_LABELS.other,
      four_legs_pillar: "Application",
      routed_seat: "AE",
      routing_reason: "Updated by staff reviewer",
      review_status: "pending_triage",
    }),
    ...patch,
    ...(patch.detected_borrower_name
      ? { borrower_name: patch.detected_borrower_name }
      : {}),
    ...(patch.borrower_name
      ? { detected_borrower_name: patch.borrower_name }
      : {}),
    ...(nextDocTypeLabel ? { doc_type_label: nextDocTypeLabel } : {}),
    reviewed_at: new Date().toISOString(),
  });

  try {
    const res = await api<SmartDropItem | { item?: SmartDropItem }>(
      `/api/smart-drops/items/${encodeURIComponent(itemId)}`,
      {
        method: "PATCH",
        body: patch,
      },
    );
    const candidate =
      res && typeof res === "object" && "item" in res && res.item
        ? res.item
        : (res as SmartDropItem | undefined);
    if (
      candidate &&
      typeof candidate.id === "string" &&
      (typeof candidate.filename === "string" ||
        typeof candidate.file_name === "string")
    ) {
      const merged = normalizeSmartDropItem({ ...optimistic, ...candidate });
      memoryItems.set(itemId, merged);
      return merged;
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400) {
      throw err;
    }
    // Keep optimistic update in local memory when offline
  }
  memoryItems.set(itemId, optimistic);
  return optimistic;
}

export const PROACTIVE_LOE_KIND_LABELS: Record<string, string> = {
  loe_credit_inquiry: "Inline LOE · Credit Inquiries",
  loe_large_deposit: "Inline LOE · Large Deposit / Wire Source",
  urla_1003_originator: "1003 Originator & Borrower e-Sign",
  loe_condition_clearing: "Inline LOE · Condition Clearing",
};

export interface CreateNativeSignRequestInput {
  item?: SmartDropItem | null;
  item_id?: string | null;
  borrower_name?: string;
  signer_role?: PortalRole;
  document_kind?: string;
  document_title?: string;
  requested_by_role?: UploaderRole;
  loan_id?: string | null;
  person_id?: string | null;
  token?: string | null;
}

function resolveDefaultSignTitle(
  documentKind: string,
  borrowerName: string,
  fallbackFilename?: string,
): string {
  const label =
    PROACTIVE_LOE_KIND_LABELS[documentKind] ??
    `Native e-Sign · ${documentKind}`;
  if (fallbackFilename) {
    return `${label} (${borrowerName} · ${fallbackFilename})`;
  }
  return `${label} (${borrowerName})`;
}

export async function createNativeSignRequest(
  itemOrInput: SmartDropItem | CreateNativeSignRequestInput,
  extraOptions?: Partial<CreateNativeSignRequestInput>,
): Promise<NativeSignRequestItem> {
  const isItem =
    "file_name" in itemOrInput ||
    ("doc_category" in itemOrInput && "routed_seat" in itemOrInput);
  const sourceItem: SmartDropItem | null = isItem
    ? (itemOrInput as SmartDropItem)
    : (itemOrInput.item ??
      (itemOrInput.item_id
        ? (memoryItems.get(itemOrInput.item_id) ?? null)
        : null));
  const opts: CreateNativeSignRequestInput = isItem
    ? { item: sourceItem, ...extraOptions }
    : { ...itemOrInput, ...extraOptions };

  const borrower_name =
    opts.borrower_name?.trim() ||
    sourceItem?.detected_borrower_name ||
    sourceItem?.borrower_name ||
    "Unassigned Borrower";
  const document_kind =
    opts.document_kind ||
    sourceItem?.proactive_loe_kind ||
    (typeof sourceItem?.extracted_highlights?.proactive_loe_kind === "string"
      ? sourceItem.extracted_highlights.proactive_loe_kind
      : null) ||
    (sourceItem?.doc_category === "mismo_1003" ||
    sourceItem?.doc_category === "1003_mismo"
      ? "urla_1003_originator"
      : sourceItem?.doc_category === "credit_report"
        ? "loe_credit_inquiry"
        : sourceItem?.doc_category === "bank_statements" ||
            sourceItem?.doc_category === "bank_statement"
          ? "loe_large_deposit"
          : "loe_condition_clearing");
  const signer_role: PortalRole =
    opts.signer_role ??
    (document_kind === "urla_1003_originator" ? "broker_lo" : "borrower");
  const document_title =
    opts.document_title ||
    resolveDefaultSignTitle(
      document_kind,
      borrower_name,
      sourceItem?.file_name,
    );
  const requested_by_role: UploaderRole =
    opts.requested_by_role ?? sourceItem?.routed_seat ?? "CA";
  const loan_id = opts.loan_id ?? sourceItem?.loan_id ?? null;
  const person_id = opts.person_id ?? sourceItem?.person_id ?? null;

  try {
    const res = await api<
      | (NativeSignRequestItem & { signer_name?: string })
      | { sign_request?: NativeSignRequestItem & { signer_name?: string } }
    >("/api/smart-drops/sign-requests", {
      method: "POST",
      body: {
        signer_name: borrower_name,
        signer_role,
        document_kind,
        document_title,
        status: "pending",
        item_id: sourceItem?.id ?? opts.item_id ?? null,
        loan_id,
        person_id,
        token: opts.token ?? sourceItem?.token ?? null,
        requested_by_role,
      },
    });
    const candidate =
      res && typeof res === "object" && "sign_request" in res && res.sign_request
        ? res.sign_request
        : (res as (NativeSignRequestItem & { signer_name?: string }) | undefined);
    if (
      candidate &&
      typeof candidate.id === "string" &&
      typeof candidate.document_kind === "string"
    ) {
      const resolvedToken =
        candidate.token ||
        opts.token ||
        sourceItem?.token ||
        `lsdrop_${candidate.id.replace(/-/g, "").slice(0, 16)}`;
      const merged: NativeSignRequestItem = {
        id: candidate.id,
        borrower_name:
          candidate.borrower_name || candidate.signer_name || borrower_name,
        signer_role: candidate.signer_role || signer_role,
        document_kind: candidate.document_kind,
        document_title: candidate.document_title || document_title,
        status: candidate.status || "pending",
        signature_seal_sha256: candidate.signature_seal_sha256 ?? null,
        created_at: candidate.created_at || new Date().toISOString(),
        sign_url: candidate.sign_url || buildSmartSignUrl(resolvedToken),
        item_id: candidate.item_id ?? sourceItem?.id ?? opts.item_id ?? null,
        loan_id: candidate.loan_id ?? loan_id,
        person_id: candidate.person_id ?? person_id,
        token: resolvedToken,
        requested_by_role: candidate.requested_by_role ?? requested_by_role,
      };
      memorySignRequests.set(merged.id, merged);
      return merged;
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400) {
      throw err;
    }
    // Offline fallback: create localReq in memorySignRequests
  }

  const linkToken =
    opts.token ||
    sourceItem?.token ||
    makeLocalLink({
      portal_role: signer_role,
      person_id,
      loan_id,
      borrower_name,
      label: `${borrower_name} · Native e-Sign`,
    }).token;

  const sign_url = buildSmartSignUrl(linkToken);
  const localReq: NativeSignRequestItem = {
    id: `sdsign_${idCounter++}`,
    borrower_name,
    signer_role,
    document_kind,
    document_title,
    status: "pending",
    signature_seal_sha256: null,
    created_at: new Date().toISOString(),
    sign_url,
    item_id: sourceItem?.id ?? opts.item_id ?? null,
    loan_id,
    person_id,
    token: linkToken,
    requested_by_role,
  };
  memorySignRequests.set(localReq.id, localReq);
  return localReq;
}

export const requestNativeSignatureOrLoe = createNativeSignRequest;

export async function executeStaffNativeSign(
  target: string | SmartDropItem | NativeSignRequestItem,
  options?: {
    signer_name?: string;
    signer_role?: UploaderRole;
    item_id?: string | null;
  },
): Promise<{
  sign_request: NativeSignRequestItem;
  item: SmartDropItem | null;
  signature_seal_sha256: string;
  seal_label: string;
}> {
  let req: NativeSignRequestItem | undefined;
  let item: SmartDropItem | null = null;

  if (typeof target === "string") {
    req = memorySignRequests.get(target);
    item =
      memoryItems.get(target) ??
      (req?.item_id ? (memoryItems.get(req.item_id) ?? null) : null);
  } else if ("sign_url" in target) {
    req = target;
    item = target.item_id ? (memoryItems.get(target.item_id) ?? null) : null;
  } else {
    item = target;
    req = [...memorySignRequests.values()].find((r) => r.item_id === item?.id);
  }

  if (!req) {
    req = await createNativeSignRequest(
      item ?? {
        borrower_name: "Borrower",
        document_kind: "loe_condition_clearing",
        requested_by_role: options?.signer_role ?? "CA",
      },
    );
  }

  const signerRole: UploaderRole =
    options?.signer_role ?? req.requested_by_role ?? "CA";
  const signerName =
    options?.signer_name ?? UPLOADER_ROLE_LABELS[signerRole] ?? "LS2 Staff";
  const signature_seal_sha256 = deterministicHexHash(
    `${req.id}:${req.borrower_name}:${req.document_kind}:${signerName}`,
  );
  const seal_label = `LS2-SEAL-${signature_seal_sha256.slice(0, 12).toUpperCase()}`;

  const signedReq: NativeSignRequestItem = {
    ...req,
    status: "signed",
    signature_seal_sha256,
    signed_by: signerName,
    signed_at: new Date().toISOString(),
  };

  try {
    await api("/api/smart-drops/sign-requests", {
      method: "POST",
      body: {
        ...signedReq,
        signer_name: signedReq.borrower_name,
        action: "execute_staff_sign",
        seal_label,
      },
    });
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400) {
      throw err;
    }
    // Offline fallback: signed state persisted in memorySignRequests & memoryItems
  }

  memorySignRequests.set(signedReq.id, signedReq);

  const targetItemId = options?.item_id ?? item?.id ?? signedReq.item_id;
  if (targetItemId) {
    const existingItem = memoryItems.get(targetItemId) ?? item;
    if (existingItem) {
      item = await updateSmartDropItem(targetItemId, {
        native_signature_seal: seal_label,
        extracted_highlights: {
          ...(existingItem.extracted_highlights ?? {}),
          native_signature_seal: seal_label,
          ls2_native_signature: seal_label,
          signature_seal_sha256,
        },
      });
    }
  }

  return {
    sign_request: signedReq,
    item,
    signature_seal_sha256,
    seal_label,
  };
}

export function getCachedNativeSignRequests(): NativeSignRequestItem[] {
  return [...memorySignRequests.values()];
}

export async function listNativeSignRequests(): Promise<NativeSignRequestItem[]> {
  try {
    const res = await api<
      | (NativeSignRequestItem & { signer_name?: string })[]
      | { sign_requests?: (NativeSignRequestItem & { signer_name?: string })[] }
    >("/api/smart-drops/sign-requests");
    const list = Array.isArray(res)
      ? res
      : Array.isArray(res?.sign_requests)
        ? res.sign_requests
        : [];
    for (const r of list) {
      if (r?.id) {
        const fallbackToken = r.token || `lsdrop_${r.id.replace(/-/g, "").slice(0, 16)}`;
        const normalized: NativeSignRequestItem = {
          ...r,
          borrower_name: r.borrower_name || r.signer_name || "Borrower",
          sign_url: r.sign_url || buildSmartSignUrl(fallbackToken),
        };
        memorySignRequests.set(normalized.id, normalized);
      }
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400) {
      throw err;
    }
    // Offline fallback
  }
  return [...memorySignRequests.values()];
}

export interface AuditCertificatePdfParams {
  request_id?: string | null;
  borrower_name?: string | null;
  signer_email?: string | null;
  document_title?: string | null;
  signature_type?: "drawn" | "typed" | string | null;
  document_sha256?: string | null;
  seal_sha256?: string | null;
}

/**
 * Builds the public token-authenticated endpoint URL for downloading a Tamper-Evident Audit
 * Certificate PDF (`GET /api/sign/:token/certificate.pdf`).
 */
export function buildAuditCertificatePdfUrl(
  tokenOrEnvelopeId: string,
  params?: AuditCertificatePdfParams,
): string {
  const cleanToken = tokenOrEnvelopeId.trim() || "demo_token";
  const q = new URLSearchParams();
  if (params?.request_id) q.set("request_id", params.request_id);
  if (params?.borrower_name) q.set("borrower_name", params.borrower_name);
  if (params?.signer_email) q.set("signer_email", params.signer_email);
  if (params?.document_title) q.set("document_title", params.document_title);
  if (params?.signature_type) q.set("signature_type", params.signature_type);
  if (params?.document_sha256) q.set("document_sha256", params.document_sha256);
  if (params?.seal_sha256) q.set("seal_sha256", params.seal_sha256);
  const qs = q.toString();
  return `/api/sign/${encodeURIComponent(cleanToken)}/certificate.pdf${qs ? `?${qs}` : ""}`;
}

export interface PosFourLegsHandoffPayload {
  borrower_name?: string | null;
  occupancy?: string | null;
  loan_purpose?: string | null;
  property_state?: string | null;
  property_type?: string | null;
  estimated_credit_score?: string | number | null;
  income_doc_type?: string | null;
  requested_loan_amount?: string | null;
  estimated_property_value?: string | null;
  reserves_months?: string | number | null;
  lo_name?: string | null;
  company_name?: string | null;
  person_id?: string | null;
  token?: string | null;
}

/**
 * Builds a deep link to `/prospecting?pos_handoff=1&...` that opens the Four Legs Scenario Desk
 * pre-populated with the Co-Branded POS intake scenario facts (`lending_decision: not_made`).
 */
export function buildPosFourLegsHandoffUrl(
  payload: PosFourLegsHandoffPayload,
  origin?: string,
): string {
  const q = new URLSearchParams();
  q.set("pos_handoff", "1");
  if (payload.borrower_name?.trim()) q.set("borrower_name", payload.borrower_name.trim());
  if (payload.occupancy?.trim()) q.set("occupancy", payload.occupancy.trim());
  if (payload.loan_purpose?.trim()) q.set("loan_purpose", payload.loan_purpose.trim());
  if (payload.property_state?.trim()) q.set("property_state", payload.property_state.trim());
  if (payload.property_type?.trim()) q.set("property_type", payload.property_type.trim());
  if (
    payload.estimated_credit_score !== undefined &&
    payload.estimated_credit_score !== null &&
    String(payload.estimated_credit_score).trim() !== ""
  ) {
    q.set("estimated_credit_score", String(payload.estimated_credit_score).trim());
  }
  if (payload.income_doc_type?.trim()) q.set("income_doc_type", payload.income_doc_type.trim());
  if (payload.requested_loan_amount?.trim()) {
    q.set("requested_loan_amount", payload.requested_loan_amount.trim());
  }
  if (payload.estimated_property_value?.trim()) {
    q.set("estimated_property_value", payload.estimated_property_value.trim());
  }
  if (
    payload.reserves_months !== undefined &&
    payload.reserves_months !== null &&
    String(payload.reserves_months).trim() !== ""
  ) {
    q.set("reserves_months", String(payload.reserves_months).trim());
  }
  if (payload.lo_name?.trim()) q.set("lo_name", payload.lo_name.trim());
  if (payload.company_name?.trim()) q.set("company_name", payload.company_name.trim());
  if (payload.person_id?.trim()) q.set("person_id", payload.person_id.trim());
  if (payload.token?.trim()) q.set("token", payload.token.trim());
  const path = `/prospecting?${q.toString()}`;
  return origin ? `${origin.replace(/\/$/, "")}${path}` : path;
}

/**
 * Infers structured POS scenario intake facts from a borrower group's uploaded Smart Drop items
 * without inventing any underwriting guidelines, thresholds, or pricing.
 */
export function inferPosHandoffFromItems(
  borrowerName: string,
  items: SmartDropItem[],
  extra?: Partial<PosFourLegsHandoffPayload>,
): PosFourLegsHandoffPayload {
  const combinedText = items
    .map((i) => `${i.file_name} ${i.doc_type_label} ${i.notes ?? ""}`)
    .join(" ")
    .toLowerCase();

  const hasDscrOrLease =
    /\b(lease|rent[ _-]?roll|dscr|investor|investment|llc|holdings)\b/.test(combinedText);
  const hasBankStmts =
    items.some(
      (i) =>
        i.doc_category === "bank_statements" ||
        i.doc_category === "bank_statement" ||
        i.laya_family === "bank_statement",
    ) || /\b(12mo|24mo|bank[ _-]?stmt|bank[ _-]?statement)\b/.test(combinedText);
  const hasFullDoc =
    items.some(
      (i) => i.doc_category === "tax_returns" || i.doc_category === "income_docs",
    ) || /\b(1040|w2|w-2|tax[ _-]?return)\b/.test(combinedText);
  const hasPnl = /\b(p&l|pnl|profit[ _-]?and[ _-]?loss)\b/.test(combinedText);

  const occupancy =
    extra?.occupancy ??
    (hasDscrOrLease
      ? "Investment"
      : /\bsecond[ _-]?home\b/.test(combinedText)
        ? "Second Home"
        : "Primary Residence");

  const loan_purpose =
    extra?.loan_purpose ??
    (/\bcash[ _-]?out\b/.test(combinedText)
      ? "Cash-Out Refi"
      : /\brefi|refinance\b/.test(combinedText)
        ? "Rate/Term Refi"
        : "Purchase");

  const income_doc_type =
    extra?.income_doc_type ??
    (hasBankStmts
      ? "Bank Statements"
      : hasDscrOrLease
        ? "DSCR / Investor Cash Flow"
        : hasPnl
          ? "P&L"
          : hasFullDoc
            ? "Full Doc"
            : "Bank Statements");

  let property_state = extra?.property_state ?? null;
  if (!property_state) {
    for (const item of items) {
      const addr =
        (typeof item.extracted_highlights?.property_address === "string"
          ? item.extracted_highlights.property_address
          : null) ?? "";
      const stateMatch = addr.match(/,\s*([A-Z]{2})\b/);
      if (stateMatch?.[1]) {
        property_state = stateMatch[1];
        break;
      }
    }
  }

  let reserves_months: string | number | null = extra?.reserves_months ?? null;
  if (reserves_months === null) {
    const stmtCount = items.filter(
      (i) =>
        i.doc_category === "bank_statements" ||
        i.doc_category === "bank_statement" ||
        i.laya_family === "bank_statement" ||
        Boolean(i.statement_period ?? i.extracted_highlights?.statement_period),
    ).length;
    if (stmtCount > 0) {
      reserves_months = String(stmtCount);
    }
  }

  const firstWithLo = items.find((i) => i.lo_name || i.company_name || i.person_id);

  return {
    borrower_name: extra?.borrower_name ?? borrowerName,
    occupancy,
    loan_purpose,
    property_state: property_state ?? "CA",
    property_type:
      extra?.property_type ??
      (/\bcondo\b/.test(combinedText) ? "Condo" : "SFR"),
    estimated_credit_score: extra?.estimated_credit_score ?? null,
    income_doc_type,
    requested_loan_amount: extra?.requested_loan_amount ?? null,
    estimated_property_value: extra?.estimated_property_value ?? null,
    reserves_months,
    lo_name: extra?.lo_name ?? firstWithLo?.lo_name ?? null,
    company_name: extra?.company_name ?? firstWithLo?.company_name ?? null,
    person_id: extra?.person_id ?? firstWithLo?.person_id ?? null,
    token: extra?.token ?? firstWithLo?.token ?? null,
  };
}

